import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  serialize,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { isEmpty } from 'lodash-es';
import { useApi } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import type {
  ICreateSearchHistoryPayload,
  ISearchHistory,
} from '~/purchase/types';

type Options = {
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<ISearchHistory>>,
      IErrorApiResponse
    >
  > & {
    args: {
      type: ISearchHistory['type'];
    };
  };
  create?: UseMutationOptions<
    ISuccessApiResponse<ISearchHistory>,
    IErrorApiResponse,
    ICreateSearchHistoryPayload
  >;
};

const prepareQuery = (
  query: ICreateSearchHistoryPayload,
): ICreateSearchHistoryPayload => {
  return {
    ...query,
    query: {
      ...query.query,
      loadIndexes: query.query.loadIndexes?.filter(
        (loadIndex) => loadIndex > 0,
      ),
      speedIndexes: query.query.speedIndexes?.filter(
        (speedIndex) => !isEmpty(speedIndex),
      ),
    },
  };
};

export const useManageSearchHistory = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;
  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.searchHistory.base(
      idCustomer,
      options.list?.args.type as string,
    ),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<Array<ISearchHistory>>>(
            `/offers/api/customers/${idCustomer}/search-history?${serialize(
              options.list?.args,
            )}`,
          )
        : emptyQueryError(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: false,
    ...options.list,
  });
  const createMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ISearchHistory>>(
            `/offers/api/customers/${idCustomer}/search-history`,
            { method: 'POST', body: JSON.stringify(prepareQuery(variables)) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: (variables) => [
      PurchaseQueryKeys.searchHistory.base(idCustomer, variables.type),
    ],
    retry: false,
    ...options.create,
  });

  return restrictApi(options, {
    list: listQuery,
    create: createMutation,
  });
};
